/**
 * The buckets My Gear's "All" view groups products into, on web and in ns-myfender: hardware
 * first, then software, then extensions, then anything else. Before this, "All" was one list in
 * the user's sort order, and an FSP member's dozens of sound sets buried their hardware and
 * software (DENG-1346).
 *
 * The bucket comes from `categoryId`. PreSonus products carry one of software, hardware,
 * extension, firmware or subscription. Registered Fender gear is FMIC SKU records, and paeapi
 * derives its categoryId from the FMIC product type (`mapCategory` in paeapi's
 * `src/libraries/fenderProducts.ts`, `determineFenderCategory` in `src/libraries/fender.ts`),
 * which emit exactly the five ids in FENDER_GEAR_CATEGORY_IDS. All of those are physical gear,
 * so they bucket as hardware.
 */

export type GearBucket = "hardware" | "software" | "extension" | "other";

/** The order buckets appear in. */
export const GEAR_BUCKET_ORDER: GearBucket[] = ["hardware", "software", "extension", "other"];

/** Fender gear categoryIds (see above). Each is physical gear, so each counts as hardware. */
export const FENDER_GEAR_CATEGORY_IDS: string[] = ["electric_guitars", "acoustic_guitars", "amplifiers", "pedals", "instruments_amps"];

/**
 * The bucket a product belongs in. Firmware, subscriptions, a missing categoryId and anything
 * unrecognised all go to "other", which sorts last.
 */
export function toGearBucket(categoryId: string | null | undefined): GearBucket {
  if (!categoryId) return "other";
  if (categoryId === "hardware" || FENDER_GEAR_CATEGORY_IDS.indexOf(categoryId) > -1) return "hardware";
  if (categoryId === "software") return "software";
  if (categoryId === "extension") return "extension";
  return "other";
}

export interface GearBucketGroup<T> {
  bucket: GearBucket;
  items: T[];
}

/**
 * Splits `items` into buckets in GEAR_BUCKET_ORDER, leaving out empty buckets. Order within a
 * bucket is the order of `items`, so the caller's sort (by date or title) carries through.
 */
export function groupByGearBucket<T>(items: T[], getCategoryId: (item: T) => string | null | undefined): GearBucketGroup<T>[] {
  const byBucket: Record<GearBucket, T[]> = { hardware: [], software: [], extension: [], other: [] };
  for (const item of items || []) {
    byBucket[toGearBucket(getCategoryId(item))].push(item);
  }
  return GEAR_BUCKET_ORDER.filter((bucket) => byBucket[bucket].length).map((bucket) => ({ bucket, items: byBucket[bucket] }));
}
